import { PasswordInput } from "@/components/ui/PasswordInput";
import React, { useState } from 'react';
import { VStack, HStack, Text, Button } from '@chakra-ui/react';
import Drawer, { DrawerHeader, DrawerTitle, DrawerBody, DrawerFooter, DrawerCloseTrigger } from '@/components/ui/Drawer';
import withHashDrawer from '@/components/ui/withHashDrawer';
import { useChangeUserPasswordMutation, useGetUserByIdQuery } from '../api';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId?: number;
  onSuccess?: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onClose,
  entityId,
  onSuccess,
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const { data: user } = useGetUserByIdQuery(entityId!, {
    skip: !entityId,
  });
  
  const [changePassword, { isLoading }] = useChangeUserPasswordMutation();

  const handleSubmit = async () => {
    if (!user) return;
    
    setError('');
    
    if (!newPassword.trim()) {
      setError('Password is required');
      return;
    }
    
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      await changePassword({
        id: user.id,
        password: newPassword,
      }).unwrap();
      
      setNewPassword('');
      setConfirmPassword('');
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err.data?.message || 'Failed to change password');
    }
  };

  const handleClose = () => {
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    onClose();
  };

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} size="md">
      <DrawerHeader>
        <DrawerTitle>Change Password</DrawerTitle>
        <DrawerCloseTrigger />
      </DrawerHeader>
      
      <DrawerBody>
        <VStack gap={4} align="stretch">
          {user && (
            <Text fontSize="sm" color="gray.600">
              Changing password for: <strong>{user.first_name} {user.last_name}</strong>
            </Text>
          )}
          
          <div>
            <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={1}>
              New Password <Text as="span" color="red.500">*</Text>
            </Text>
            <PasswordInput
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
            />
          </div>
          
          <div>
            <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={1}>
              Confirm Password <Text as="span" color="red.500">*</Text>
            </Text>
            <PasswordInput
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
            />
          </div>
          
          {error && (
            <Text color="red.500" fontSize="sm">
              {error}
            </Text>
          )}
          
          <Text fontSize="sm" color="gray.600">
            Password must be at least 8 characters long.
          </Text>
        </VStack>
      </DrawerBody>
      
      <DrawerFooter>
        <HStack gap={3}>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={isLoading}
            disabled={isLoading}
            colorPalette="purple"
          >
            Change Password
          </Button>
        </HStack>
      </DrawerFooter>
    </Drawer>
  );
};

export default withHashDrawer(ChangePasswordModal, {
  action: 'change-password',
});
